/**
 * Feature scroller
 *
 * Progressive enhancement for the feature-scroller block. The markup is fully
 * functional without JavaScript (the nav is a list of in-page anchor links);
 * this element adds smooth scrolling and keeps the nav in sync with whichever
 * feature is currently in view.
 */
class FeatureScroller extends HTMLElement {
  #controller = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number} */
  #activeIndex = -1;

  /** @type {HTMLAnchorElement[]} */
  get #links() {
    return /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('[data-feature-link]')]);
  }

  /** @type {HTMLElement[]} */
  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-feature-item]')]);
  }

  connectedCallback() {
    const { signal } = this.#controller;
    const items = this.#items;

    if (!items.length) return;

    this.addEventListener('click', this.#handleClick, { signal });

    // Without IntersectionObserver the nav still navigates; just mark the first
    // item so the active state is never empty.
    if (!('IntersectionObserver' in window)) {
      this.#setActive(0);
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      rootMargin: '-45% 0px -45% 0px',
    });

    for (const item of items) this.#observer.observe(item);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }

  /**
   * Scrolls to the feature a nav link points at.
   * @param {MouseEvent} event - The click event.
   */
  #handleClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const link = target?.closest('[data-feature-link]');

    if (!(link instanceof HTMLAnchorElement)) return;

    const index = Number(link.dataset.index);
    const item = this.#items[index];

    if (!item) return;

    event.preventDefault();

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    item.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });

    // Move focus so keyboard and screen reader users follow the scroll.
    item.focus({ preventScroll: true });
  };

  /**
   * @param {IntersectionObserverEntry[]} entries - The observed entries.
   */
  #handleIntersect = (entries) => {
    const items = this.#items;

    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      this.#setActive(items.indexOf(/** @type {HTMLElement} */ (entry.target)));
    }
  };

  /**
   * Marks the nav link at `index` as current.
   * @param {number} index - Index of the active feature.
   */
  #setActive(index) {
    if (index < 0 || index === this.#activeIndex) return;

    this.#activeIndex = index;

    this.#links.forEach((link, i) => {
      const isActive = i === index;

      link.classList.toggle('is-active', isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('feature-scroller')) {
  customElements.define('feature-scroller', FeatureScroller);
}
